import React from 'react';
import Settings from '@mui/icons-material/Settings';
import Logs from '@mui/icons-material/Assessment';
import Metrics from '@mui/icons-material/Timeline';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
// import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'; // Reporting
// import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined'; // Departments
// import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined'; // Workflow
// import GavelOutlined from '@mui/icons-material/GavelOutlined'; // Compliance — enable after proposals
import Logout from '@mui/icons-material/Logout';
// import NoteAddOutlined from '@mui/icons-material/NoteAddOutlined'; // Import — enable after proposals
import * as S from './AdminButtons.styled';
import { useAppSelector } from 'redux/hooks';
import { selectPermission } from 'redux/global';
import { useHistory, useLocation } from 'react-router-dom';
import { clearMainBG } from 'pages/Navigation/utils';
import {
  canAccessCommandCenterNav,
  canAccessFormsNav,
  canAccessMasterDataNav,
  canAccessActivityLogsNav,
  canAccessMetricsNav,
  // canAccessCompanyDepartmentsPage, // Departments
  // canAccessWorkflowPage, // Workflow
  // canAccessDepartmentsPage, // Import — enable after proposals
} from 'pages/Settings/redux';
import { TrippleLogo } from 'pages/components/LFHeader/LFHeader';
import {
  ERP_PATHS,
  erpSettingsPath,
  getErpModuleFromPath,
  isActivityLogsPagePath,
  isErpPagePath,
  isErpSettingsAccount,
} from '../erpModulePaths';
import { ACTIVITY_LOGS_LABEL_SHORT } from 'pages/Logs/activityLogLabels';
import { useFormsInboxCounts } from 'pages/Forms/formsInboxCountsHook';

/** Navigate in-app on primary click; leave modified clicks to open the real href. */
const useSidebarNav = () => {
  const history = useHistory();
  return (href: string, e: React.MouseEvent<HTMLAnchorElement>, state?: object) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    clearMainBG();
    if (state) history.push(href, state);
    else history.push(href);
  };
};

type RailLink = {
  href: string,
  label: string,
  Icon: React.ElementType,
  isSelected: boolean,
  badgeCount?: number,
};

const AdminButtons = function AdminButtons() {
  const permission = useAppSelector(selectPermission);
  const history = useHistory();
  const location = useLocation();
  const nav = useSidebarNav();
  const moduleId = getErpModuleFromPath(location.pathname) || 'reporting_analytics';
  const { badgeTotal: formsBadgeTotal } = useFormsInboxCounts(canAccessFormsNav(permission));
  const settingsHref = erpSettingsPath(moduleId);
  const settingsAccountHref = erpSettingsPath(moduleId, 'account');

  const onClickAppBrand = () => {
    clearMainBG();
    if (canAccessCommandCenterNav(permission)) {
      history.push(ERP_PATHS.commandCenter);
      return;
    }
    if (canAccessFormsNav(permission)) {
      history.push(ERP_PATHS.forms);
      return;
    }
    history.push(ERP_PATHS.records);
  };

  const links: RailLink[] = [
    ...(canAccessCommandCenterNav(permission) ? [{
      href: ERP_PATHS.commandCenter,
      label: 'Command center',
      Icon: HubOutlined,
      isSelected: isErpPagePath(location.pathname, 'command-center'),
    }] : []),
    ...(canAccessFormsNav(permission) ? [{
      href: ERP_PATHS.forms,
      label: 'Forms',
      Icon: DescriptionOutlined,
      isSelected: isErpPagePath(location.pathname, 'forms'),
      badgeCount: formsBadgeTotal,
    }] : []),
    ...(canAccessMasterDataNav(permission) ? [{
      href: ERP_PATHS.records,
      label: 'Master data',
      Icon: FolderOutlined,
      isSelected: isErpPagePath(location.pathname, 'records'),
    }] : []),
    ...(canAccessActivityLogsNav(permission) ? [{
      href: ERP_PATHS.activityLogs,
      label: ACTIVITY_LOGS_LABEL_SHORT,
      Icon: Logs,
      isSelected: isActivityLogsPagePath(location.pathname),
    }] : []),
    ...(canAccessMetricsNav(permission) ? [{
      href: ERP_PATHS.metrics,
      label: 'Metrics',
      Icon: Metrics,
      isSelected: isErpPagePath(location.pathname, 'metrics'),
    }] : []),
    // {
    //   href: ERP_PATHS.reporting,
    //   label: 'Reporting',
    //   Icon: DescriptionOutlined,
    //   isSelected: isErpPagePath(location.pathname, 'reporting'),
    // },
    // ...(canAccessCompanyDepartmentsPage(permission) ? [{
    //   href: ERP_PATHS.orgDepartments,
    //   label: 'Departments',
    //   Icon: ApartmentOutlined,
    //   isSelected: isErpPagePath(location.pathname, 'departments')
    //     && getErpModuleFromPath(location.pathname) === 'reporting_analytics',
    // }] : []),
    // ...(canAccessWorkflowPage(permission) ? [{
    //   href: ERP_PATHS.workflow,
    //   label: 'Workflow',
    //   Icon: AccountTreeOutlined,
    //   isSelected: isErpPagePath(location.pathname, 'workflow'),
    // }] : []),
    // Import — enable after proposals
    // ...(canAccessDepartmentsPage(permission) ? [{
    //   href: ERP_PATHS.departments,
    //   label: 'Import',
    //   Icon: NoteAddOutlined,
    //   isSelected: isErpPagePath(location.pathname, 'departments'),
    // }] : []),
  ];

  return (
    <S.Container id="flex_bar">
      <S.AppLogo
        type="button"
        aria-label="Tripple home"
        onClick={onClickAppBrand}
      >
        <TrippleLogo showText />
      </S.AppLogo>
      <S.Icons>
        {links.map(({ href, label, Icon, isSelected, badgeCount }) => {
          const badgeLabel = badgeCount && badgeCount > 99 ? '99+' : String(badgeCount || '');
          return (
          <a
            key={href}
            href={href}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={(e) => nav(href, e)}
            aria-label={badgeCount ? `${label}, ${badgeCount} pending` : label}
          >
            <S.Icon isSelected={isSelected}>
              <S.IconGlyphWrap>
                <Icon fontSize="small" />
                {badgeCount ? <S.FormsNavBadgeDot aria-hidden /> : null}
              </S.IconGlyphWrap>
              <S.IconText>
                {label}
              </S.IconText>
              {badgeCount ? <S.FormsNavBadge>{badgeLabel}</S.FormsNavBadge> : null}
            </S.Icon>
          </a>
          );
        })}
      </S.Icons>
      <S.Divider />
      <S.Icons2>
        <a
          href={settingsHref}
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={(e) => nav(settingsHref, e)}
        >
          <S.Icon isSelected={
            isErpPagePath(location.pathname, 'settings')
            && !isErpSettingsAccount(location.pathname)
          }
          >
            <Settings fontSize="small" />
            <S.IconText>
              Settings
            </S.IconText>
          </S.Icon>
        </a>
        <a
          href={settingsAccountHref}
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={(e) => nav(settingsAccountHref, e, { openLogout: true })}
        >
          <S.Icon isSelected={false}>
            <Logout fontSize="small" />
            <S.IconText>
              Sign out
            </S.IconText>
          </S.Icon>
        </a>
      </S.Icons2>
    </S.Container>
  );
};

export default React.memo(AdminButtons);
